import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Select from "react-select";
import Swal from "sweetalert2";

const MODULO = "Centros";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const RegistrarCentros = ({ actualizarCentros }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [codCentro, setCodCentro] = useState("");
  const [tiposedeCentro, setTiposedeCentro] = useState("");
  const [nombreanteriorCentro, setNombreanteriorCentro] = useState("");
  const [nombreCentro, setNombreCentro] = useState("");
  const [nombreAgrupacionCentro, setNombreAgrupacionCentro] = useState("");
  const [municipioCentro, setMunicipioCentro] = useState("");
  const [direccionCentro, setDireccionCentro] = useState("");
  const [codRegional, setCodRegional] = useState("");

  const [regionales, setRegionales] = useState([]);
  const [selectedRegional, setSelectedRegional] = useState(null);

  const [loading, setLoading] = useState(false);

  const isEdit = useMemo(() => Boolean(id), [id]);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloCentros = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloCentros = useMemo(() => getModuloCentros(), []);

  const puedeCrear =
    moduloCentros?.puedeCrear === true ||
    moduloCentros?.crear === true ||
    moduloCentros?.create === true;

  const puedeActualizar =
    moduloCentros?.puedeActualizar === true ||
    moduloCentros?.actualizar === true ||
    moduloCentros?.editar === true ||
    moduloCentros?.update === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const cleanData = () => {
    setCodCentro("");
    setTiposedeCentro("");
    setNombreanteriorCentro("");
    setNombreCentro("");
    setNombreAgrupacionCentro("");
    setMunicipioCentro("");
    setDireccionCentro("");
    setCodRegional("");
    setSelectedRegional(null);
  };

  const obtenerRegionales = useCallback(async () => {
    try {
      const response = await axios.get("/api/regionales/listall", authConfig());

      if (response.data.ok) {
        const ordenadas = (response.data.regionales || []).sort((a, b) =>
          (a?.nombreRegional || "").localeCompare(b?.nombreRegional || "")
        );

        setRegionales(ordenadas);
      } else {
        Swal.fire(
          "Error",
          response.data.message || "No se pudo cargar regionales",
          "error"
        );

        setRegionales([]);
      }
    } catch (error) {
      console.error("Error de red:", error?.message);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudo cargar regionales", "error");
      setRegionales([]);
    }
  }, [manejarErrorSesion]);

  const regionalOptions = useMemo(
    () =>
      regionales.map((regional) => ({
        value: regional.nombreRegional,
        label: regional.nombreRegional,
        codRegional: regional.codRegional,
        id: regional._id,
      })),
    [regionales]
  );

  const getData = useCallback(async () => {
    try {
      const response = await axios.get(
        `/api/centrosdeformacion/listid/${id}`,
        authConfig()
      );

      if (response.data.ok) {
        const centro = response.data.centro;

        setCodCentro(centro.codCentro ?? "");
        setTiposedeCentro(centro.tiposedeCentro ?? "");
        setNombreanteriorCentro(centro.nombreanteriorCentro ?? "");
        setNombreCentro(centro.nombreCentro ?? "");
        setNombreAgrupacionCentro(centro.nombreAgrupacionCentro ?? "");
        setMunicipioCentro(centro.municipioCentro ?? "");
        setDireccionCentro(centro.direccionCentro ?? "");
        setCodRegional(centro.codRegional ?? "");

        const regionalEncontrada = regionales.find(
          (r) =>
            r.codRegional === centro.codRegional ||
            r._id === centro.regionalObjectId ||
            r._id === centro.regionalObjectId?._id
        );

        setSelectedRegional(
          regionalEncontrada
            ? {
                value: regionalEncontrada.nombreRegional,
                label: regionalEncontrada.nombreRegional,
                codRegional: regionalEncontrada.codRegional,
                id: regionalEncontrada._id,
              }
            : null
        );
      }
    } catch (error) {
      console.error("Error al obtener datos del centro:", error.message);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudo cargar el centro de formación", "error");
    }
  }, [id, regionales, manejarErrorSesion]);

  const saveCentro = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para registrar centros.",
        "warning"
      );

      return;
    }

    try {
      setLoading(true);

      const newCentro = {
        codCentro,
        tiposedeCentro,
        nombreanteriorCentro,
        nombreCentro,
        nombreAgrupacionCentro,
        municipioCentro,
        direccionCentro,
        codRegional,
        regionalObjectId: selectedRegional?.id || null,
      };

      await axios.post("/api/centrosdeformacion/add", newCentro, authConfig());

      cleanData();

      Swal.fire({
        icon: "success",
        title: "Centro de Formación registrado con éxito",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => navigate("/institucional/centro/"));

      actualizarCentros?.();
    } catch (error) {
      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al guardar",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const updateCentro = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para actualizar centros.",
        "warning"
      );

      return;
    }

    try {
      setLoading(true);

      const updatedCentro = {
        codCentro,
        tiposedeCentro,
        nombreanteriorCentro,
        nombreCentro,
        nombreAgrupacionCentro,
        municipioCentro,
        direccionCentro,
        codRegional,
        regionalObjectId: selectedRegional?.id || null,
      };

      await axios.put(
        `/api/centrosdeformacion/update/${id}`,
        updatedCentro,
        authConfig()
      );

      cleanData();

      Swal.fire({
        icon: "success",
        title: "Centro de formación actualizado con éxito",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => navigate("/institucional/centro/"));

      actualizarCentros?.();
    } catch (error) {
      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al actualizar",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegionalChange = (selectedOption) => {
    setSelectedRegional(selectedOption);
    setCodRegional(selectedOption?.codRegional || "");
  };

  const actions = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!codCentro || !nombreCentro.trim()) {
      Swal.fire("Atención", "Código y Nombre Centro son obligatorios.", "warning");
      return;
    }

    isEdit ? await updateCentro() : await saveCentro();
  };

  const handleCancel = () => {
    cleanData();
    navigate("/institucional/centro/");
  };

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      await obtenerRegionales();
      setLoading(false);
    };

    cargar();
  }, [obtenerRegionales]);

  useEffect(() => {
    const cargarEdicion = async () => {
      if (!id || regionales.length === 0) return;

      setLoading(true);
      await getData();
      setLoading(false);
    };

    cargarEdicion();
  }, [id, getData, regionales.length]);

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
        boxShadow: state.isFocused
          ? "0 0 0 .25rem rgba(13,110,253,.25)"
          : "none",
        ":hover": {
          borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
        },
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    }),
    []
  );

  const puedeGuardarFormulario = isEdit ? puedeActualizar : puedeCrear;

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit
                    ? "Editar Centro de Formación"
                    : "Registrar Centro de Formación"}
                </h5>

                <small className="text-secondary">
                  Completa la información del centro y asócialo a una regional.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate("/institucional/centro/")}
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" />
                <div className="mt-3">Cargando datos…</div>
              </div>
            ) : (
              <form onSubmit={actions}>
                <div className="row g-3">
                  <div className="col-12 col-md-2">
                    <label className="form-label fw-semibold">Código *</label>

                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa-solid fa-hashtag"></i>
                      </span>

                      <input
                        className="form-control text-uppercase"
                        value={codCentro}
                        onChange={(e) => setCodCentro(e.target.value)}
                        required
                        maxLength={10}
                        placeholder="Ej: 12"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold">Tipo Sede</label>

                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa-solid fa-map-pin"></i>
                      </span>

                      <input
                        className="form-control"
                        value={tiposedeCentro}
                        onChange={(e) => setTiposedeCentro(e.target.value)}
                        placeholder="Ej: Principal / Subsede"
                        maxLength={40}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold">
                      Nombre Anterior
                    </label>

                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa-solid fa-clock-rotate-left"></i>
                      </span>

                      <input
                        className="form-control"
                        value={nombreanteriorCentro}
                        onChange={(e) =>
                          setNombreanteriorCentro(e.target.value)
                        }
                        placeholder="(Opcional)"
                        maxLength={80}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold">
                      Nombre Centro *
                    </label>

                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa-solid fa-building-columns"></i>
                      </span>

                      <input
                        className="form-control"
                        value={nombreCentro}
                        onChange={(e) => setNombreCentro(e.target.value)}
                        placeholder="Ej: Centro de Servicios…"
                        maxLength={120}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label fw-semibold">
                      Nombre Agrupación
                    </label>

                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa-solid fa-layer-group"></i>
                      </span>

                      <input
                        className="form-control"
                        value={nombreAgrupacionCentro}
                        onChange={(e) =>
                          setNombreAgrupacionCentro(e.target.value)
                        }
                        placeholder="(Opcional)"
                        maxLength={120}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-2">
                    <label className="form-label fw-semibold">Municipio</label>

                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa-solid fa-city"></i>
                      </span>

                      <input
                        className="form-control"
                        value={municipioCentro}
                        onChange={(e) => setMunicipioCentro(e.target.value)}
                        placeholder="Ej: Medellín"
                        maxLength={60}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold">Dirección</label>

                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fa-solid fa-location-dot"></i>
                      </span>

                      <input
                        className="form-control"
                        value={direccionCentro}
                        onChange={(e) => setDireccionCentro(e.target.value)}
                        placeholder="Ej: Calle 10 # 20-30"
                        maxLength={120}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold">Regional</label>

                    <Select
                      options={regionalOptions}
                      value={selectedRegional}
                      onChange={handleRegionalChange}
                      placeholder="Seleccione una regional"
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isDisabled={loading}
                    />
                  </div>

                  <div className="col-12 mt-3">
                    <div className="d-flex flex-column flex-md-row gap-2 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-outline-dark"
                        onClick={cleanData}
                        disabled={loading}
                        title="Limpiar"
                      >
                        <i className="fa-solid fa-eraser me-2"></i>
                        Limpiar
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        <i className="fa-solid fa-xmark me-2"></i>
                        Cancelar
                      </button>

                      {puedeGuardarFormulario && (
                        <button
                          type="submit"
                          className="btn btn-dark"
                          disabled={loading}
                          title={isEdit ? "Actualizar" : "Guardar"}
                        >
                          <i
                            className={`fa-solid ${
                              loading
                                ? "fa-spinner fa-spin"
                                : "fa-floppy-disk"
                            } me-2`}
                          ></i>

                          {loading
                            ? isEdit
                              ? "Actualizando…"
                              : "Guardando…"
                            : isEdit
                            ? "Actualizar"
                            : "Guardar"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarCentros;